import { Fragment } from "preact";
import Link, { Button, ButtonList } from "../../components/link";
import AffiliateDisclosure from "../../components/affiliate-disclosure";
import Center from "../../components/center";
import Editorial from "../../components/editorial";
import Head from "../../components/head";
import Hero from "../../components/hero";
import Accordion from "../../components/accordion";
import AccordionPreview from "../../components/accordion/preview";
import ImageSet from "../../components/image-set";
import Image from "../../components/image";
import Author from "../../components/author";
import NoWrap from "../../components/no-wrap";
import SlugHeading from "../../components/slug-heading";
import SlugLink from "../../components/slug-link";
import ReviewItem from "../../components/review-item";
import Tags from "../../components/tags";
import Questions from "../../components/questions";
import namedRoutes from "../../settings/routes";
import { lookupByUrl } from "../../settings/route-data-lookup";

const headerInfo = lookupByUrl(namedRoutes.velit.heater);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionY={92}
      background="/images/backgrounds/snowboarding.webp"
      titlePrefix="Velit"
      title="Gasoline Heater"
      subTitle="Vanlife Review"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <Image
          showAlt
          right
          src="/images/products/velit/heater-4000P.webp"
          alt="Velit Gasoline Heater"
        />

        <p>
          I installed the Velit Gasoline Air Heater hoping it would handle cold
          winter nights and high elevation. It’s rated at 14,000 BTU and built
          for both gas and diesel vehicles, which made it a good fit for my Ford
          Transit.
        </p>
        <p>
          After over two years of regular use, I have a good sense of its
          reliability, quirks, and overall value. My review covers how it
          performed in real conditions, what installation involved, and whether
          I’d recommend it.
        </p>

        <ImageSet
          showAlt
          size={5}
          shadow={true}
          images={[
            {
              lg: "/images/products/velit/install/lg/unboxing.webp",
              sm: "/images/products/velit/install/sm/unboxing.webp",
              alt: "Unboxing heater as packaged",
            },
            {
              lg: "/images/products/velit/install/lg/box-content.webp",
              sm: "/images/products/velit/install/sm/box-content.webp",
              alt: "Complete contents of package",
            },
            {
              lg: "/images/products/velit/install/lg/20240622_202649943.webp",
              sm: "/images/products/velit/install/sm/20240622_202649943.webp",
              alt: "Intake vent under seat base",
            },
            {
              lg: "/images/products/velit/install/lg/20240622_201306058.webp",
              sm: "/images/products/velit/install/sm/20240622_201306058.webp",
              alt: "Heat vent on back of seat base",
            },
            {
              lg: "/images/products/velit/install/lg/20240622_200151471.webp",
              sm: "/images/products/velit/install/sm/20240622_200151471.webp",
              alt: "Original thermostat mounted on dash",
            },
          ]}
        />

        <AffiliateDisclosure brandSent={false} />

        <div style="clear:both;" />
        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Pros & Cons</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Installation</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Specifications</SlugLink>
                  </li>
                  <li>
                    <SlugLink>My Verdict</SlugLink>
                  </li>
                </ol>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Pros &amp; Cons</SlugHeading>
        <SlugHeading tag="h3">Pros</SlugHeading>
        <ul>
          <li>
            <strong>Offers a gasoline version</strong> (diesel version also
            available)
          </li>
          <li>
            <strong>Works great at high elevation</strong> (I have used at
            12,000 ft, rated to ≤17,000 ft)
          </li>
          <li>
            <strong>Heats quickly</strong> (kept me warm in 10°F (-12°C), be
            careful not to leave items in front of it)
          </li>
          <li>
            <strong>Great value</strong> (compared to Espar (Eberspächer) and
            Webasto)
          </li>
          <li>
            <strong>Quiet fuel pump</strong> (hardly noticeable inside vehicle)
          </li>
          <li>
            <strong>Efficient fuel usage</strong> (I don't notice my fuel level
            change)
          </li>
          <li>
            <strong>Includes quick connectors and standpipe</strong> (different
            install options)
          </li>
        </ul>

        <SlugHeading tag="h3">Cons</SlugHeading>
        <ul>
          <li>
            <strong>Complicated install</strong> (as with any similar heater,
            Velit also has an{" "}
            <Link
              external
              href="https://velitcamping.com/collections/air-heaters/products/velit-portable-air-heater-gasoline-diesel-14000btu-4000w?variant=47453543661805"
            >
              all‑in‑one portable heater
            </Link>
            )
          </li>
          <li>
            <strong>More expensive than cheap diesel variants</strong> (US based
            company, 2 year warranty)
          </li>
          <li>
            <strong>Had to upgrade wiring</strong> (due to low voltage error,
            simple fix)
          </li>
          <li>
            <strong>Not silent</strong> (as expected you can hear the fan when
            running)
          </li>
          <li>
            <strong>Thermostat needs improvement</strong>
            <ul class="disc">
              <li>
                Poor interface (single dial button that controls everything)
              </li>
              <li>
                Bright light at night (can't adjust brightness, screen doesn't
                timeout)
              </li>
              <li>Large temperature range</li>
              <li>
                Minimum set temperature of 60°F (I wish it could be set to 40°F)
              </li>
              <li>No Celsius option (frustrating for users outside the US)</li>
            </ul>
          </li>
          <li>
            <strong>Bubbles in fuel line</strong>
            <ul class="disc">
              <li>
                Requires manual priming after long drives or periods of
                inactivity (included standpipe might work better than accessory
                port)
              </li>
              <li>
                Difficult to know how long to manually run the fuel pump
                (sometimes takes a few tries but always starts)
              </li>
            </ul>
          </li>
          <li>
            <strong>Random errors</strong>
            <ul class="disc">
              <li>
                Occasionally errors out and eventually turns off (not sure if
                it's due to air bubbles or something else)
              </li>
              <li>Periodically needs reset (by unplugging screen)</li>
            </ul>
          </li>
        </ul>

        <div id="velit-feedback">
          <Accordion
            headingsAs="h3"
            items={[
              {
                heading: "My feedback to Velit",
                content: (
                  <Fragment>
                    <p>
                      The following feedback is directed to Velit. I will update
                      it after I review the Bluetooth thermostat:
                    </p>
                    <ul>
                      <li>
                        <strong>Differentiate:</strong> It's quite clear that
                        this product is derived from the expired Espar patents
                        similar to the many diesel heater clones. I understand
                        Velit has done a lot of research and product development
                        to enhance this design by improving the combustion
                        chamber etc. However, I feel Velit needs to further this
                        approach and differentiate itself more so it is very
                        clear to potential customers.
                        <ul>
                          <li>
                            For the next version of the heater don't make it
                            look like a clone (even though it's not); at the
                            very least redesign the plastic case (make sure to
                            consider easier access for maintenance).
                          </li>
                          <li>
                            There are diesel heaters available on Amazon for
                            $100. Your website should make it very clear what
                            the value add of this product is since it costs over
                            $600 more.
                          </li>
                        </ul>
                      </li>
                      <li>
                        <strong>Heater controls:</strong> The main ways a
                        customer is going to interact with your product are via
                        the controls on the thermostat or in the app. Make these
                        experiences as enjoyable as possible and visually
                        appealing while still blending in. You made great
                        progress on this with the new thermostat by improving
                        the dial/button interface and making it black.
                      </li>
                      <li>
                        <strong>Error codes:</strong> I understand that
                        providing error codes might be necessary on a small
                        screen, however please also provide short descriptions
                        especially for the common ones such as ignition failure.
                        This will reduce the time customers need to reference
                        the user manual (which they might not have with them)
                        for error codes.
                      </li>
                      <li>
                        <strong>Reduce air bubbles:</strong> If there is
                        anything you can do to help prevent air bubbles in the
                        design of the heater, fuel line, etc. it would
                        drastically improve the reliability of this product.
                      </li>
                      <li>
                        <strong>Separate temperature sensor:</strong> By
                        combining the thermostat and controls in a single unit,
                        it sometimes restricts the placement to non-preferred
                        locations (you don't want temperature sensor near path
                        of where hot air blows). If the temperature sensor was
                        separate from the control case or there was an option
                        for a separate secondary temperature sensor (which when
                        plugged in is used instead of the sensor in the control
                        case), it would allow for ideal placement of both the
                        controls and the temperature sensor.
                      </li>
                      <li>
                        <strong>Website:</strong> Your{" "}
                        <Link
                          external
                          href="https://velitcamping.com/collections/air-heaters/products/velit-gasoline-air-heater-17000btu"
                        >
                          website
                        </Link>{" "}
                        is a primary location where your customers will go to
                        learn about your products to inform their purchasing
                        decisions.
                        <ul>
                          <li>
                            The only indication that there is more than a single
                            photo are the right/left arrows. Add thumbnails
                            underneath, as most sites do on their product pages.
                          </li>
                          <li>
                            Consider adding direct comparisons to Espar,
                            Webasto, and other knockoff heaters and show why
                            your product is great.
                          </li>
                          <li>
                            Add a few more product photos at different angles.
                          </li>
                          <li>Add photos of it installed in a few vehicles.</li>
                          <li>
                            The app photos on the heater page are for your air
                            conditioner not the heater.
                          </li>
                        </ul>
                      </li>
                      <li>
                        <strong>Heater parts:</strong> When looking for
                        replacement parts on your{" "}
                        <Link
                          external
                          href="https://velitcamping.com/collections/air-heaters"
                        >
                          air heaters
                        </Link>{" "}
                        category page it looks as if only the fuel pump is
                        available for purchase. At minimum, update this
                        thumbnail to represent multiple parts. Having parts
                        available to order is important and something customers
                        value.
                        <ul>
                          <li>
                            When on the{" "}
                            <Link
                              external
                              href="https://velitcamping.com/collections/heater-parts"
                            >
                              parts product page
                            </Link>{" "}
                            there are 23 different options listed; only 3
                            contain photos and none contain a description or any
                            specifications. For the ones that do have photos,
                            the photos are not directly linked to the items so
                            you have to guess and infer which you might be
                            looking at.
                          </li>
                          <li>
                            The Bluetooth thermostat is listed at $125, which is
                            more than an entire cheap diesel heater knockoff on
                            Amazon. If possible, consider lowering the prices on
                            parts and plan on making your income on main
                            products.
                          </li>
                          <li>Move shipping and air fees to separate page.</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Product manual:</strong> There could be a lot of
                        improvements to the manual (I'm not going to go into
                        most of these here). It is your job to ensure customers
                        have the information they need to reliably install your
                        product correctly. If a customer unintentionally
                        installs it incorrectly, they will usually conclude your
                        product is bad or faulty not that they didn't understand
                        one of the steps. This could result in negative reviews
                        which prevent others from purchasing your product all
                        because someone didn't understand part of the install.
                        The install guide should be written to help the process
                        go as smoothly as possible. It should break things down
                        into easy-to-follow steps, while also providing enough
                        clarity to answer advanced questions for non-typical
                        installs.
                        <ul>
                          <li>
                            The manual on the website and the manual that came
                            with my product are both listed as version 1.3,
                            however they are different.
                          </li>
                          <li>
                            Portions of the current manual seem to be a copied
                            or heavily inspired directly out of Espar or other
                            diesel heater manuals (
                            <Link
                              external
                              href="https://www.butlertechnik.com/downloads/Eberspacher_Airtronic_D2_Technical_Manual.pdf"
                            >
                              example
                            </Link>
                            ) and the diagrams don't even match the appearance
                            of Velit parts.
                          </li>
                          <li>
                            The website still offers{" "}
                            <Link
                              external
                              href="https://cdn.shopify.com/s/files/1/0623/2840/6253/files/4000P_Air_Heater_User_Manual1.3.pdf?v=1681614528"
                            >
                              version 1.3{" "}
                            </Link>{" "}
                            of the manual which is the same version I used in
                            spring of 2024 but the current manual features the
                            new thermostat.
                          </li>
                        </ul>
                      </li>
                      <li>
                        <strong>Customer engagement:</strong> Monitor and engage
                        in your Facebook group and on other social platforms.
                        When I first got my heater, Velit had an employee
                        monitoring and engaging in the Facebook group, but that
                        no longer seems to be the case.
                      </li>
                      <li>
                        <strong>Clearer specs:</strong>
                        <ul>
                          <li>
                            Cooling capacity is listed as 14000BTU, which I
                            believe should say heating capacity.
                          </li>
                          <li>
                            The URL of the page contains 17000btu which I
                            believe should be 14000btu.
                          </li>
                          <li>
                            Specs list volts and watts, but not amps. Amps is
                            important for determining both fuse and wire size.
                          </li>
                          <li>
                            Doesn't list lengths of included items: fuel line,
                            exhaust pipe, intake hose, outlet hose, fuel pickup,
                            power cable, fuel pump cable, thermostat cable.
                          </li>
                          <li>Unclear if Bluetooth can be disabled.</li>
                          <li>
                            No listed service agreement for the app or firmware
                            updates.
                          </li>
                          <li>Add metric equivalents.</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Maintenance:</strong> It's inevitable that
                        long-term owners of your products will need to perform
                        maintenance at some point, sometimes even while on the
                        road. Please consider this in your product designs.
                        Adding easy disconnects near the heater for wires
                        (power, fuel pump, and thermostat) would be helpful. A
                        maintenance guide or video could be useful in supporting
                        these customers.
                      </li>
                      <li>
                        <strong>Install templates:</strong> Sometimes the most
                        difficult part of the install is figuring out exactly
                        where to install it and how to route the fuel line to
                        that location. Providing install templates and guides
                        for common locations in popular camper vans (Sprinter,
                        Transit, ProMaster) would be a huge benefit for your
                        customers. I made my own install template using CAD
                        files from Ford which I then printed on a transparency
                        sheet.
                      </li>
                      <li>
                        <strong>Exhaust pipe:</strong> I painted my exhaust
                        black using high temp primer and paint (2000°F). I think
                        a lot of people would be interested in a black exhaust
                        option. There are also chemical processes to accomplish
                        this, however I do not know how economical these are or
                        how they hold up to heat.
                      </li>
                      <li>
                        <strong>Current stock:</strong> It's unclear if you have
                        stock available or not. I can add the item to my cart
                        and continue to checkout with no warning, however under
                        the description on the{" "}
                        <Link
                          external
                          href="https://velitcamping.com/collections/air-heaters/products/velit-gasoline-air-heater-17000btu"
                        >
                          product page
                        </Link>{" "}
                        it says the item is "backordered" for the US and
                        "special order" for Canada and UK.
                      </li>
                      <li>
                        <strong>Product name:</strong> It's unclear what the
                        official product name is. As you produce future models
                        it can become confusing when people are referring to a
                        generic Velit heater instead of the specific model. This
                        is also the case with the heater's thermostat (original
                        vs Bluetooth).
                        <ul>
                          <li>Velit Gasoline Air Heater</li>
                          <li>Velit 4000P Gasoline Air Heater</li>
                          <li>GAS400012V</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Location:</strong> Where are you located? Your
                        about page says you design and test your products in
                        Seattle, WA, but your contact page and marketing emails
                        list an address in Spokane, WA.
                      </li>
                    </ul>
                  </Fragment>
                ),
              },
            ]}
          />
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">New thermostat</SlugHeading>
        <Image
          showAlt
          right
          src="/images/products/velit/4000p-thermostat.webp"
          alt="New Velit heater thermostat"
        />
        <p>
          I reached out to Velit about reviewing the new thermostat and they
          replied the same day and offered to send me an upgrade kit. One of
          their employees happens to live nearby so I will be meeting them soon.
          They said the new thermostat should solve many of my concerns. I plan
          to document the upgrade process and provide a comparison to the older
          model.
        </p>
        <p>
          The new smart thermostat now comes standard and is equipped with
          built-in carbon monoxide and flammable gas sensors. It also adds
          Bluetooth, allowing you to control your heater from up to 100 feet
          away using your phone.
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Installation</SlugHeading>
        <p>
          It requires you to drop the vehicle's fuel tank, route the fuel line,
          cut a hole in the floor for the heater, mount the fuel pump, route air
          intake, and mount the exhaust. Care needs to be taken as it deals with
          both an electrical item but also flammable fuel near very hot items
          such as the vehicle's exhaust shield.
        </p>
        <p>
          I made several custom brackets for various items to make the install
          better and safer. The following photos document much of my process,
          although a few steps are missing as I was in a hurry to get on the
          road and enjoy the snow.
        </p>

        <ImageSet
          showAlt
          maxShow={8}
          shadow={true}
          images={[
            /*
            Missing photos:
            - fuel tank
            - fuel line over exhaust shield
            - fuel line to fuel pump
            - installed from underneath
            - wiring upgrade
            */
            {
              lg: "/images/products/velit/install/lg/20240313_172627547.webp",
              sm: "/images/products/velit/install/sm/20240313_172627547.webp",
              alt: "Front seat of Ford Transit removed",
            },
            {
              lg: "/images/products/velit/install/lg/20240317_180832198.webp",
              sm: "/images/products/velit/install/sm/20240317_180832198.webp",
              alt: "Custom template for accurate position of all holes",
            },
            {
              lg: "/images/products/velit/install/lg/20240317_200405312.webp",
              sm: "/images/products/velit/install/sm/20240317_200405312.webp",
              alt: "Intake/exhaust hole cut",
            },
            {
              lg: "/images/products/velit/install/lg/20240317_012047206.webp",
              sm: "/images/products/velit/install/sm/20240317_012047206.webp",
              alt: "Making custom heater stand with aluminum C-channel",
            },
            {
              lg: "/images/products/velit/install/lg/20240318_222713370.webp",
              sm: "/images/products/velit/install/sm/20240318_222713370.webp",
              alt: "Ensuring I have access to bolts with a screwdriver",
            },
            {
              lg: "/images/products/velit/install/lg/20240319_004721989.webp",
              sm: "/images/products/velit/install/sm/20240319_004721989.webp",
              alt: "Making custom plate for heater stand",
            },
            {
              lg: "/images/products/velit/install/lg/20240320_172909666.webp",
              sm: "/images/products/velit/install/sm/20240320_172909666.webp",
              alt: "Dry fitting custom plate and heater stand",
            },
            {
              lg: "/images/products/velit/install/lg/20240320_193833437.webp",
              sm: "/images/products/velit/install/sm/20240320_193833437.webp",
              alt: "Dry fitting heater stand",
            },
            {
              lg: "/images/products/velit/install/lg/20240321_195717387.webp",
              sm: "/images/products/velit/install/sm/20240321_195717387.webp",
              alt: "Cutting flanges off seat base which get in the way",
            },
            {
              lg: "/images/products/velit/install/lg/20240324_165418725.webp",
              sm: "/images/products/velit/install/sm/20240324_165418725.webp",
              alt: "Custom silicone foam gasket and more RTV silicone than was needed",
            },

            // Vent hole
            {
              lg: "/images/products/velit/install/lg/20240320_213231575.webp",
              sm: "/images/products/velit/install/sm/20240320_213231575.webp",
              alt: "Template to cut hole cut in back of seat base",
            },
            {
              lg: "/images/products/velit/install/lg/20240320_215507436.webp",
              sm: "/images/products/velit/install/sm/20240320_215507436.webp",
              alt: "Hole cut in back of seat base",
            },
            {
              lg: "/images/products/velit/install/lg/20240320_215542288.webp",
              sm: "/images/products/velit/install/sm/20240320_215542288.webp",
              alt: "Vent in back of seat base",
            },

            // Air intake
            {
              lg: "/images/products/velit/install/lg/20240322_172618873.webp",
              sm: "/images/products/velit/install/sm/20240322_172618873.webp",
              alt: "Making custom air intake splash guard from broken garbage can",
            },
            {
              lg: "/images/products/velit/install/lg/20240323_001728886.webp",
              sm: "/images/products/velit/install/sm/20240323_001728886.webp",
              alt: "Bent plastic using a heat gun to make air intake splash guard",
            },
            {
              lg: "/images/products/velit/install/lg/20240324_010138615.webp",
              sm: "/images/products/velit/install/sm/20240324_010138615.webp",
              alt: "Painted and installed custom splash guard for air intake",
            },
            {
              lg: "/images/products/velit/install/lg/20240324_182304019.webp",
              sm: "/images/products/velit/install/sm/20240324_182304019.webp",
              alt: "Air intake with custom mounting bracket",
            },
            {
              lg: "/images/products/velit/install/lg/20240324_183013692.webp",
              sm: "/images/products/velit/install/sm/20240324_183013692.webp",
              alt: "Air intake installed",
            },

            // Fuel pump
            {
              lg: "/images/products/velit/install/lg/20240326_224913910.webp",
              sm: "/images/products/velit/install/sm/20240326_224913910.webp",
              alt: "Custom fuel pump hanger to reduce vibrations",
            },
            {
              lg: "/images/products/velit/install/lg/20240622_195708092.webp",
              sm: "/images/products/velit/install/sm/20240622_195708092.webp",
              alt: "Fuel pump installed with custom hanger to reduce vibrations",
            },

            // Exhaust
            {
              lg: "/images/products/velit/install/lg/20240322_170922180.webp",
              sm: "/images/products/velit/install/sm/20240322_170922180.webp",
              alt: "Custom exhaust hanger riveted in place",
            },
            {
              lg: "/images/products/velit/install/lg/20240622_195733873.webp",
              sm: "/images/products/velit/install/sm/20240622_195733873.webp",
              alt: "Muffler and exhaust installed",
            },
            {
              lg: "/images/products/velit/install/lg/20240330_151949336.webp",
              sm: "/images/products/velit/install/sm/20240330_151949336.webp",
              alt: "Painted black exhaust installed",
            },

            {
              lg: "/images/products/velit/install/lg/20240324_203436113.webp",
              sm: "/images/products/velit/install/sm/20240324_203436113.webp",
              alt: "Test fitting with polyiso insulation on floor",
            },

            {
              lg: "/images/products/velit/install/lg/20240320_181224854.webp",
              sm: "/images/products/velit/install/sm/20240320_181224854.webp",
              alt: "Cutting heat vent at correct angle",
            },
            {
              lg: "/images/products/velit/install/lg/20240320_182311264.webp",
              sm: "/images/products/velit/install/sm/20240320_182311264.webp",
              alt: "Test fitting heat vent",
            },

            {
              lg: "/images/products/velit/install/lg/20240622_201306058.webp",
              sm: "/images/products/velit/install/sm/20240622_201306058.webp",
              alt: "Heat vent on back of seat base",
            },

            {
              lg: "/images/products/velit/install/lg/20240622_202649943.webp",
              sm: "/images/products/velit/install/sm/20240622_202649943.webp",
              alt: "Intake vent under seat base",
            },

            {
              lg: "/images/products/velit/install/lg/20240622_200151471.webp",
              sm: "/images/products/velit/install/sm/20240622_200151471.webp",
              alt: "Original thermostat mounted on dash",
            },

            {
              lg: "/images/products/velit/install/lg/20240326_013418822.webp",
              sm: "/images/products/velit/install/sm/20240326_013418822.webp",
              alt: "Readying connections for install",
            },
            {
              lg: "/images/products/velit/install/lg/20240326_014331576.webp",
              sm: "/images/products/velit/install/sm/20240326_014331576.webp",
              alt: "Ready connections with protective heat insulation for install",
            },
            {
              lg: "/images/products/velit/install/lg/20240327_203425177.webp",
              sm: "/images/products/velit/install/sm/20240327_203425177.webp",
              alt: "Custom support and insulation for fuel line above exhaust shield",
            },
          ]}
        />

        <Accordion
          headingsAs="h3"
          right
          items={[
            {
              heading: "Additional items used",
              content: (
                <Fragment>
                  <p>
                    Here are several of the additional items I used during the
                    install. I'm not including tools or basic hardware such as
                    bolts, nuts, and washers.
                  </p>

                  <Accordion
                    right
                    items={[
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Silicone foam"
                            heading="Silicone Heat Press Pad"
                            src="/images/products/heat-mat.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used as a compressible gasket between the custom
                              heater stand and aluminum plate.
                            </p>

                            <ButtonList>
                              <Button
                                affiliate="https://amzn.to/4pbxACd"
                                href="https://www.amazon.com/dp/B09ZKRFFVC"
                                secondary
                                icon="Amazon"
                              >
                                Buy on Amazon
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Aluminum channel"
                            heading="Aluminum Channel 6063"
                            src="/images/products/aluminum/aluminum-channel.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to form the heater stand to elevate the
                              heater off the floor. This helped it be inline
                              without needs bends in the hot air vent.
                            </p>
                            <p>
                              <strong>Size:</strong> 1.5 x 1 in
                              <br />
                              <strong>Thickness:</strong> 1/8 in
                            </p>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Aluminum sheet"
                            heading="Aluminum Sheet 5052"
                            src="/images/products/aluminum/aluminum-sheet.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to make a new larger mounting plate to fit
                              the heater stand.
                            </p>
                            <p>
                              <strong>Thickness:</strong> 1/8 in
                            </p>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="RTV silicone"
                            heading="J-B Weld Hi-Temp Silicone"
                            src="/images/products/j-b-weld-rtv.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to create a water and fume proof seal for the
                              base on the heater stand.
                            </p>

                            <ButtonList>
                              <Button
                                affiliate="https://amzn.to/48juJjZ"
                                href="https://www.amazon.com/dp/B00ID8IUJY"
                                secondary
                                icon="Amazon"
                              >
                                Buy on Amazon
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Steel reinforced epoxy"
                            heading="J-B Weld"
                            src="/images/products/j-b-weld.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to attach the corners on the aluminum stand
                              and fill in the crack near the rivets.
                            </p>

                            <ButtonList>
                              <Button
                                affiliate="https://amzn.to/3Xz4dhb"
                                href="https://www.amazon.com/dp/B014OVHAOY"
                                secondary
                                icon="Amazon"
                              >
                                Buy on Amazon
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Fuel line insulation"
                            heading="Heat sheath"
                            src="/images/products/heat-sheath.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to insulate the fuel lines when near the
                              vehicle’s exhaust shield and the heaters exhaust.
                            </p>

                            <ButtonList>
                              <Button
                                affiliate="https://amzn.to/3WW73g3"
                                href="https://www.amazon.com/dp/B000CEM3M2"
                                secondary
                                icon="Amazon"
                              >
                                Buy on Amazon
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Wire loom"
                            heading="Protective Wire Wrap"
                            src="/images/products/wire-wrap.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to protect the fuel line and the fuel pump
                              wiring.
                            </p>

                            <ButtonList>
                              <Button
                                external
                                href="https://www.harborfreight.com/hardware/cable-ties/wire-wrap/1-4-quarter-inch-x-14-ft-protective-wire-wrap-66985.html"
                              >
                                Buy from Harbor Freight
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Zip-ties"
                            heading="Heat resistant cable ties"
                            src="/images/products/heat-resistant-cable-ties.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used for attaching various parts of the fuel line
                              and air intake to the vehicle.
                            </p>

                            <ButtonList>
                              <Button
                                external
                                href="https://www.homedepot.com/p/Commercial-Electric-11-in-Heat-Resistant-Cable-Tie-20-Pack-GT-280STH/315460459"
                                icon="Amazon"
                              >
                                Buy from Home Depot
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Black exhaust paint"
                            heading="Rust-Oleum High Heat Ceramic Coating"
                            src="/images/products/rust-oleum-high-heat.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to make the visible part of the exhaust black
                              instead of shiny silver. This paint required
                              baking in an oven.
                            </p>

                            <ButtonList>
                              <Button
                                affiliate="https://amzn.to/49TLSTr"
                                href="https://www.amazon.com/dp/B003CT4AKC/"
                                secondary
                                icon="Amazon"
                              >
                                Buy on Amazon
                              </Button>
                            </ButtonList>

                            <Accordion
                              right
                              items={[
                                {
                                  preview: (
                                    <AccordionPreview
                                      headingAs="h5"
                                      prefix="Exhaust primer"
                                      heading="Rust-Oleum High Heat Primer"
                                      src="/images/products/rust-oleum-high-heat-primer.webp"
                                    />
                                  ),
                                  content: (
                                    <Fragment>
                                      <ButtonList>
                                        <Button
                                          affiliate="https://amzn.to/4i1xfj1"
                                          href="https://www.amazon.com/dp/B003CT499O/"
                                          secondary
                                          icon="Amazon"
                                        >
                                          Buy on Amazon
                                        </Button>
                                      </ButtonList>
                                    </Fragment>
                                  ),
                                },
                              ]}
                            />
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Exhaust Insulation"
                            heading="Fiberglass Exhaust Header Wrap"
                            src="/images/products/exhaust-wrap.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to insulate the heater's exhaust when near
                              the vehicle.
                            </p>
                            <ButtonList>
                              <Button
                                affiliate="https://amzn.to/47QtSqt"
                                href="https://www.amazon.com/dp/B0C545N6SM"
                                secondary
                                icon="Amazon"
                              >
                                Buy on Amazon
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Double-sided tape"
                            heading="3M VHB tape"
                            src="/images/products/3m/3m-vhb.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to help attach a bracket to the vehicle which
                              helps route the fuel line over the exhaust shield.
                            </p>
                            <ButtonList>
                              <Button
                                affiliate="https://amzn.to/3XqTLbB"
                                href="https://www.amazon.com/dp/B0CQ1WDP3S/"
                                secondary
                                icon="Amazon"
                              >
                                Buy on Amazon
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Automotive fasteners"
                            heading="Plastic fastener kit"
                            src="/images/products/auto-fasteners.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used to help attach a bracket to the vehicle which
                              helps route the fuel line over the exhaust shield.
                            </p>
                            <ButtonList>
                              <Button
                                external
                                href="https://www.harborfreight.com/automotive-plastic-fastener-kit-240-piece-57671.html"
                              >
                                Buy from Harbor Freight
                              </Button>
                            </ButtonList>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Aluminum flat bar"
                            heading="Aluminum Flat Bar 6063 - 0.5 in"
                            src="/images/products/aluminum/aluminum-flat-bar.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>
                              Used for:
                              <ul>
                                <li>
                                  Supporting fuel line over the vehicle’s
                                  exhaust shield
                                </li>
                                <li>Bracket for air intake</li>
                                <li>Bracket for exhaust hanger</li>
                              </ul>
                            </p>
                            <p>
                              <strong>Size:</strong> 1/2 in <br />
                              <strong>Thickness:</strong> 1/16 in
                            </p>
                          </Fragment>
                        ),
                      },
                      {
                        preview: (
                          <AccordionPreview
                            headingAs="h4"
                            prefix="Aluminum flat bar"
                            heading="Aluminum Flat Bar 6063 - 1.5 in"
                            src="/images/products/aluminum/aluminum-flat-bar-1_5.webp"
                          />
                        ),
                        content: (
                          <Fragment>
                            <p>Used for fuel pump hanger.</p>
                            <p>
                              <strong>Size:</strong> 1 1/2 in <br />
                              <strong>Thickness:</strong> 1/8 in
                            </p>
                          </Fragment>
                        ),
                      },
                    ]}
                  />
                </Fragment>
              ),
            },
          ]}
        />

        <SlugHeading tag="h3">Wiring upgrade</SlugHeading>
        <p>
          The heater worked great during the first long winter trip. However, we
          noticed it having trouble starting occasionally and figured it was
          just due to air bubbles in the line from driving. However, months
          later it would fail to start and we determined it was due to low
          voltage. To fix this, we upgraded the main wiring.
        </p>

        <ImageSet
          showAlt
          size={3}
          shadow={true}
          images={[
            {
              lg: "/images/products/velit/install/lg/20241009_195459032_1.webp",
              sm: "/images/products/velit/install/sm/20241009_195459032_1.webp",
              alt: "Using a multimeter to check the startup voltage",
            },
            {
              lg: "/images/products/velit/install/lg/20241009_195459032_2.webp",
              sm: "/images/products/velit/install/sm/20241009_195459032_2.webp",
              alt: "Checking startup voltage directly on ECU",
            },
            {
              lg: "/images/products/velit/install/lg/20241012_162758691.webp",
              sm: "/images/products/velit/install/sm/20241012_162758691.webp",
              alt: "10AWG wiring upgrade complete",
            },
          ]}
        />

        <Accordion
          right
          items={[
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Marine grade wire"
                  heading="10-2 Duplex Wire"
                  src="/images/products/ancor-marine-10-2awg.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    Due to low voltage on startup (error code 01 and 03), I
                    upgraded all but a few inches of my wiring harness from the
                    original 14AWG to marine grade 10AWG. I used around 14 feet
                    total. After upgrading the wiring, I have not encountered
                    the issue again.
                  </p>
                  <p>
                    <strong>Brand:</strong> Ancor
                    <br />
                    <strong>Model:</strong> 121125
                    <br />
                    <strong>Gauge:</strong> 10AWG
                  </p>
                  <p>
                    Exceeds all UL 1426, US Coast Guard Charter boat (CFR Title
                    46) and ABYC standards.
                  </p>
                  <ButtonList>
                    <Button
                      external
                      href="https://www.westmarine.com/ancor-flat-duplex-wire-by-the-foot-P009_274_003_002.html"
                    >
                      Buy from West Marine
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Lever nuts"
                  heading="Wago 221-612 Lever-Nuts 10AWG"
                  src="/images/products/wago-221-612.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    Used to splice together different wire sizes (in this case,
                    14AWG to 10AWG). From now on I plan to use these everywhere
                    in my build. They are so simple, convenient, and worth the
                    higher price.
                  </p>
                  <p>
                    <strong>Max Current:</strong> 30A
                    <br />
                    <strong>AWG Range:</strong> 20-10AWG
                  </p>
                  <p>
                    They even have a spot to stick a multimeter without having
                    to disconnect anything, which is helpful when
                    troubleshooting.
                  </p>

                  <AffiliateDisclosure brandSent={false} />

                  <ButtonList>
                    <Button
                      affiliate="https://amzn.to/4nQqcuR"
                      href="https://www.amazon.com/dp/B085P2WT8K/"
                      secondary
                      icon="Amazon"
                    >
                      Buy on Amazon
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Specifications</SlugHeading>
        <ul>
          <li>
            <strong>Price:</strong> $729
          </li>
          <li>
            <strong>Fuel type:</strong> gasoline / diesel
          </li>
          <li>
            <strong>Heat output:</strong> 14,000 BTU
          </li>
          <li>
            <strong>Operating temperature:</strong>{" "}
            <NoWrap>-30°F to 75°F</NoWrap> <NoWrap>(-34°C to 24°C)</NoWrap>
          </li>
          <li>
            <strong>Operating elevation:</strong> ≤17,000 ft
          </li>
          <li>
            <strong>Voltage:</strong> 11-16V DC
          </li>
          <li>
            <strong>Rated power:</strong> 20-46W
          </li>
          <li>
            <strong>Fuel consumption:</strong> <NoWrap>0.05-0.13 gph</NoWrap> /{" "}
            <NoWrap>0.05-0.12 gph</NoWrap>
          </li>
          <li>
            <strong>Weight:</strong> 10lbs
          </li>
          <li>
            <strong>Dimension:</strong> 14.6 × 5.5 × 5.9 in (371 × 140 × 150 mm)
          </li>
          <li>
            <strong>Warranty:</strong> 2 years
          </li>
        </ul>

        <p>
          <Link
            external
            href="https://cdn.shopify.com/s/files/1/0623/2840/6253/files/4000P_Air_Heater_User_Manual1.3.pdf?v=1681614528"
          >
            Product manual
          </Link>
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">My Verdict</SlugHeading>
        <p>
          Overall, I enjoy the product and how it performs, especially in cold
          and high-altitude conditions. I’d definitely install it again in
          another van build.
        </p>

        <ButtonList>
          <Button
            external
            href="https://velitcamping.com/products/velit-gasoline-air-heater-17000btu?variant=43985138778349"
          >
            Buy from Velit
          </Button>
        </ButtonList>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.transit} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.powerSystems} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
    </Editorial>
  </div>
);

export default Page;
